import type { BillingInformation } from "@/interfaces/billingInformations";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import type { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { get } from "@vueuse/core";
import { MaybeRef } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  params: Params;
  immediate?: boolean;
}

interface Params extends Omit<Partial<BillingInformation>, "id"> {
  id: MaybeRef<string | number> | string | number;
}

export interface SaveCompanyBillingParams {
  companyId: string | number;
  existingId?: string | number;
  // file mode
  file?: File;
  // manual mode
  rfc?: string;
  legalName?: string;
  taxSystemId?: string | number;
  zipcode?: string;
  state?: string;
  street?: string;
  number?: string;
  internalNumber?: string;
  neighborhood?: string;
  municipality?: string;
  city?: string;
}

const getCompanyBillingInformation = async (params: Params) => {
  const { data } = await axios.get<BillingInformation[]>(
    `/api/companies/${get(params.id) || params.id}/billing-informations`,
    { errorHandling: false } as CustomAxiosRequestConfig,
  );

  return data[0] ?? null;
};

const saveCompanyBillingInformation = async (
  params: SaveCompanyBillingParams,
  toast: ToastInterface,
) => {
  try {
    if (params.file) {
      const formData = new FormData();
      formData.append("file", params.file);

      if (params.existingId) {
        formData.append("_method", "put");
        const { data } = await axios.post<BillingInformation>(
          `/api/billing-informations/${params.existingId}`,
          formData,
        );
        toast.success("La información fiscal se ha actualizado correctamente");
        return data;
      } else {
        const { data } = await axios.post<BillingInformation>(
          `/api/companies/${params.companyId}/billing-informations`,
          formData,
        );
        toast.success("La información fiscal se ha creado correctamente");
        return data;
      }
    } else {
      const payload = {
        rfc: params.rfc,
        legalName: params.legalName,
        taxSystemId: params.taxSystemId,
        zipcode: params.zipcode,
        state: params.state,
        street: params.street,
        number: params.number,
        internalNumber: params.internalNumber,
        neighborhood: params.neighborhood,
        municipality: params.municipality,
        city: params.city,
      };

      if (params.existingId) {
        const { data } = await axios.put<BillingInformation>(
          `/api/billing-informations/${params.existingId}`,
          payload,
        );
        toast.success("La información fiscal se ha actualizado correctamente");
        return data;
      } else {
        const { data } = await axios.post<BillingInformation>(
          `/api/companies/${params.companyId}/billing-informations`,
          payload,
        );
        toast.success("La información fiscal se ha creado correctamente");
        return data;
      }
    }
  } catch (e: any) {
    toast.error(e.response?.data.message);
  }
};

const useCompanyBillingInformation = (props: Props) => {
  const { params, immediate = true } = props;
  const toast = useToast();

  const { data, isLoading, isError, refetch } = useQuery(
    ["CompanyBillingInformation", params.id],
    () => getCompanyBillingInformation(params),
    {
      enabled: immediate,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const saveMutation = useMutation(
    (props: MutationParams<SaveCompanyBillingParams>) =>
      saveCompanyBillingInformation(props.params, toast),
  );

  return {
    data,
    isError,
    isLoading,
    refetch,

    saveMutation,
    save: saveMutation.mutate,
    saveAsync: saveMutation.mutateAsync,
    saveLoading: saveMutation.isLoading,
  };
};

export default useCompanyBillingInformation;
